/**
 * Mobile Leagues Page
 * Shows all user's leagues as cards
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dynastyTheme } from '../../../services/colorService';

import { leaguesAPI } from '../../../services/apiService';

const C = dynastyTheme.tokens.colors;

function MobileLeagues() {
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserLeagues();
  }, []);

  const fetchUserLeagues = async () => {
    try {
      setLoading(true);
      const response = await leaguesAPI.getMyLeagues();
      const userLeagues = response.leagues || [];
      setLeagues(userLeagues);
      setError(null);

      // Don't auto-navigate - let user see their leagues
    } catch (err) {
      console.error('Error fetching leagues:', err);
      setError(err.message || 'Failed to load leagues');
    } finally {
      setLoading(false);
    }
  };

  const handleLeagueClick = (leagueId) => {
    // Navigate to league home
    navigate(`/leagues/${leagueId}`);
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        padding: '20px',
        paddingBottom: '0',
        minHeight: '100vh',
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: `3px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
            borderTop: `3px solid ${dynastyTheme.tokens.colors.primary}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 15px'
          }}></div>
          <p style={{ color: dynastyTheme.tokens.colors.neutral[400] }}>
            Loading leagues...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        padding: '20px',
        paddingBottom: '0',
        minHeight: '100vh',
        backgroundColor: 'transparent'
      }}>
        <div style={{
          padding: '20px',
          backgroundColor: dynastyTheme.tokens.colors.error + '20',
          border: `1px solid ${dynastyTheme.tokens.colors.error}`,
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <p style={{ color: dynastyTheme.tokens.colors.error, marginBottom: '15px' }}>
            ❌ {error}
          </p>
          <button
            onClick={fetchUserLeagues}
            style={{
              padding: '10px 20px',
              backgroundColor: dynastyTheme.tokens.colors.primary,
              color: C.black,
              border: 'none',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: '15px',
      minHeight: '100vh',
      backgroundColor: 'transparent'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: C.white,
          margin: 0,
          marginBottom: '8px'
        }}>
          My Leagues
        </h1>
        <p style={{
          fontSize: '14px',
          color: dynastyTheme.tokens.colors.neutral[400],
          margin: 0
        }}>
          {leagues.length} {leagues.length === 1 ? 'league' : 'leagues'}
        </p>
      </div>

      {/* Leagues List */}
      {leagues.length === 0 ? (
        <div style={{
          padding: '40px 20px',
          textAlign: 'center',
          backgroundColor: dynastyTheme.tokens.colors.neutral[800],
          borderRadius: '8px',
          border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`
        }}>
          <div style={{ fontSize: '48px', marginBottom: '15px' }}>🏆</div>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: C.white,
            marginBottom: '10px'
          }}>
            No Leagues Yet
          </h3>
          <p style={{
            fontSize: '14px',
            color: dynastyTheme.tokens.colors.neutral[400],
            marginBottom: '20px'
          }}>
            Join or create your first league to get started
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {leagues.map((league) => (
            <div
              key={league.league_id}
              onClick={() => handleLeagueClick(league.league_id)}
              style={{
                backgroundColor: dynastyTheme.tokens.colors.neutral[800],
                borderRadius: '8px',
                padding: '15px',
                border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = 'scale(0.98)';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* League Icon */}
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '8px',
                  backgroundColor: dynastyTheme.tokens.colors.primary + '20',
                  border: `2px solid ${dynastyTheme.tokens.colors.primary}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  flexShrink: 0
                }}>
                  🏆
                </div>

                {/* League Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: C.white,
                    margin: 0,
                    marginBottom: '4px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {league.league_name || 'Unnamed League'}
                  </h3>
                  <div style={{
                    fontSize: '13px',
                    color: dynastyTheme.tokens.colors.neutral[400],
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {league.role === 'commissioner' && (
                      <span style={{ color: dynastyTheme.tokens.colors.primary }}>
                        Commissioner
                      </span>
                    )}
                  </div>
                </div>

                {/* Arrow */}
                <div style={{
                  color: dynastyTheme.tokens.colors.neutral[500],
                  fontSize: '18px',
                  flexShrink: 0
                }}>
                  →
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add keyframes for spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default MobileLeagues;
